import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  FileText, Plus, Search, Upload, AlertTriangle, CheckCircle2,
  Calendar, Trash2, Download, ExternalLink, Loader2,
} from "lucide-react";
import moment from "moment";

const CAT_LABELS = {
  license: "Llicència",
  certificate: "Certificat",
  protocol: "Protocol APPCC",
  training: "Formació",
  insurance: "Assegurança",
  supplier: "Proveïdor",
  haccp: "HACCP",
  other: "Altres",
};

const CAT_COLORS = {
  license: "bg-blue-100 text-blue-700",
  certificate: "bg-green-100 text-green-700",
  protocol: "bg-purple-100 text-purple-700",
  training: "bg-amber-100 text-amber-700",
  insurance: "bg-indigo-100 text-indigo-700",
  supplier: "bg-orange-100 text-orange-700",
  haccp: "bg-red-100 text-red-700",
  other: "bg-gray-100 text-gray-700",
};

const BLANK = {
  name: "", category: "certificate", description: "",
  issue_date: moment().format("YYYY-MM-DD"), expiry_date: "",
  file_url: "", status: "valid",
};

function getDocStatus(doc) {
  if (!doc.expiry_date) return "valid";
  const daysLeft = moment(doc.expiry_date).diff(moment(), "days");
  if (daysLeft < 0) return "expired";
  if (daysLeft <= 30) return "expiring_soon";
  return "valid";
}

export default function Documents() {
  const { currentVenue, user, userRole, loading: venueLoading } = useVenue();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) loadDocs();
  }, [currentVenue]);

  const loadDocs = async () => {
    setLoading(true);
    const items = await hosys.entities.Document.filter(
      { venue_id: currentVenue.id }, "-created_date"
    );
    // Recalculate status
    setDocs(items.map(d => ({ ...d, status: getDocStatus(d) })));
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    setForm(f => ({ ...f, file_url, name: f.name || file.name }));
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name || !form.category) return;
    setSaving(true);
    const status = getDocStatus(form);
    await hosys.entities.Document.create({
      ...form,
      venue_id: currentVenue.id,
      status,
      uploaded_by_name: user.full_name,
      uploaded_by_email: user.email,
    });
    // Create alert if expiring/expired
    if (status === "expiring_soon" || status === "expired") {
      const daysLeft = form.expiry_date ? moment(form.expiry_date).diff(moment(), "days") : null;
      await hosys.entities.Alert.create({
        venue_id: currentVenue.id,
        type: "document_expiry",
        title: status === "expired" ? `Document caducat: ${form.name}` : `Document pròxim a caducar: ${form.name}`,
        message: daysLeft !== null
          ? (daysLeft < 0 ? `El document va caducar fa ${Math.abs(daysLeft)} dies.` : `Caduca en ${daysLeft} dies (${moment(form.expiry_date).format("DD/MM/YYYY")}).`)
          : "",
        severity: status === "expired" ? "danger" : "warning",
        status: "active",
        reference_type: "Document",
      });
    }
    setForm(BLANK);
    setShowForm(false);
    setSaving(false);
    await loadDocs();
  };

  const handleDelete = async (id) => {
    await hosys.entities.Document.delete(id);
    setDocs(prev => prev.filter(d => d.id !== id));
  };

  if (venueLoading) return <QuickLoader />;

  const filtered = docs.filter(d => {
    const matchSearch = !search || d.name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === "all" || d.category === catFilter;
    const matchStatus = statusFilter === "all" || d.status === statusFilter;
    return matchSearch && matchCat && matchStatus;
  });

  const expiring = docs.filter(d => d.status === "expiring_soon").length;
  const expired = docs.filter(d => d.status === "expired").length;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Documents"
        subtitle={`${docs.length} documents · ${expiring + expired > 0 ? `⚠ ${expiring + expired} requereixen atenció` : "Tot en ordre"}`}
        actions={
          isAdmin && (
            <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl">
              <Plus className="w-4 h-4" /> Afegir document
            </Button>
          )
        }
      />

      {/* Alerts */}
      {(expired > 0 || expiring > 0) && (
        <div className="space-y-2">
          {expired > 0 && (
            <div className="bg-red-600 text-white rounded-2xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p className="font-semibold">{expired} document(s) caducat(s) — cal renovar urgentment</p>
            </div>
          )}
          {expiring > 0 && (
            <div className="bg-amber-500 text-white rounded-2xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p className="font-semibold">{expiring} document(s) pròxims a caducar (menys de 30 dies)</p>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Cercar documents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Totes</SelectItem>
            {Object.entries(CAT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="Estat" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tots els estats</SelectItem>
            <SelectItem value="valid">Vigent</SelectItem>
            <SelectItem value="expiring_soon">Pròxim a caducar</SelectItem>
            <SelectItem value="expired">Caducat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Documents list */}
      {loading ? <QuickLoader /> : filtered.length === 0 ? (
        <EmptyState icon={FileText} title="Sense documents" description="No hi ha documents amb aquests filtres."
          action={isAdmin && <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"><Plus className="w-4 h-4" />Afegir document</Button>}
        />
      ) : (
        <div className="space-y-2">
          {filtered.map(doc => {
            const daysLeft = doc.expiry_date ? moment(doc.expiry_date).diff(moment(), "days") : null;
            return (
              <Card key={doc.id} className={`p-4 border-0 shadow-sm flex items-center gap-4 ${
                doc.status === "expired" ? "border-l-4 border-red-500 bg-red-50" :
                doc.status === "expiring_soon" ? "border-l-4 border-amber-400 bg-amber-50" : ""
              }`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${CAT_COLORS[doc.category]}`}>
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{doc.name}</h3>
                    <Badge variant="secondary" className={`${CAT_COLORS[doc.category]} border-0 text-[10px]`}>
                      {CAT_LABELS[doc.category]}
                    </Badge>
                    {doc.status === "expired" && <Badge className="bg-red-600 text-white border-0 text-[10px]">Caducat</Badge>}
                    {doc.status === "expiring_soon" && <Badge className="bg-amber-500 text-white border-0 text-[10px]">Caduca en {daysLeft}d</Badge>}
                    {doc.status === "valid" && doc.expiry_date && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Vigent fins {moment(doc.expiry_date).format("DD/MM/YY")}</Badge>}
                  </div>
                  {doc.description && <p className="text-xs text-gray-500 line-clamp-1">{doc.description}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">Pujat per {doc.uploaded_by_name} · {moment(doc.created_date).format("DD/MM/YYYY")}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {doc.file_url && (
                    <a href={doc.file_url} download={doc.name} rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="gap-1">
                        <ExternalLink className="w-3 h-3" /> Descarregar
                      </Button>
                    </a>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)} className="text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Document Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setForm(BLANK); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Afegir document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom del document *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Llicència d'activitat 2024" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CAT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripció</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Descripció opcional..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data d'emissió</Label>
                <Input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} />
              </div>
              <div>
                <Label>Data de caducitat</Label>
                <Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Fitxer (PDF, imatge...)</Label>
              <div className="flex items-center gap-3 mt-1">
                <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Pujant..." : "Seleccionar fitxer"}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileUpload} disabled={uploading} />
                </label>
                {form.file_url && <span className="text-xs text-emerald-600 font-medium">✓ Fitxer pujat</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setForm(BLANK); }}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.name || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}